/**
 * COMMUNITY ITEM SERVICE
 *
 * Aggregation queries for non-polish itemTypes. Polish has its own dedicated
 * catalog (polishCatalog, maintained by triggers) so its detail page uses
 * getPolishBySlug + getPolishContributions. Every other itemType queries
 * user items directly and aggregates client-side:
 *
 *   collectionGroup('items')
 *     .where('itemType', '==', type)
 *     .where('brandSlug', '==', brandSlug)
 *     .where('nameSlug', '==', nameSlug)
 *
 * Requires the composite COLLECTION_GROUP index on
 * (itemType, brandSlug, nameSlug) — added in firestore.indexes.json.
 *
 * Note: we query by `brandSlug` (slugified brand name) rather than
 * `brandLower` (lowercased brand name) so brands with special characters
 * — apostrophes ("What's Up Nails"), ampersands ("Lantern & Wren") —
 * resolve correctly. `brandLower` and slug can diverge on those cases
 * (`"what's up nails"` vs `"whats-up-nails"`), which would cause the
 * detail page to falsely report "not found." Every item stamped by
 * addItem after Phase 1 carries brandSlug — legacy items lack it and
 * are already excluded via the missing `nameSlug`.
 *
 * Only items written after Phase 1 have `nameSlug`; legacy non-polish items
 * are invisible to this query. That's an accepted tradeoff for launch —
 * users can re-save legacy items to opt them in, and going forward every
 * new item is queryable by default.
 */

import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { ItemType } from '../types';
import type { Polish } from '../types/polish';
import type { PolishContribution, ContributorPhoto } from './polishCatalogService';

const DMCA_HIDDEN = new Set(['hidden', 'removed', 'flagged']);

/**
 * Extract displayable photos from an item, applying the same DMCA + shape
 * rules as polishCatalogService's extractPhotos. Kept here so this file is
 * self-contained rather than reaching into a polish-scoped helper.
 */
function extractPhotos(item: Record<string, any>): ContributorPhoto[] {
  const out: ContributorPhoto[] = [];
  if (Array.isArray(item.photos)) {
    for (const p of item.photos) {
      if (typeof p === 'string') {
        out.push({ url: p });
      } else if (p?.url && !DMCA_HIDDEN.has(p?.dmcaStatus)) {
        out.push({ url: p.url, caption: p.caption });
      }
    }
  }
  return out;
}

export interface CommunityItemAggregation {
  /**
   * A Polish-shaped aggregate so CommunityItemDetailPage can render both
   * polish and non-polish uniformly. Fields not applicable to the itemType
   * are left undefined; the shared page skips absent sections. Includes:
   *   - name / brand humanized-from-canonical values (pulled from the
   *     first matching item so casing matches how at least one collector
   *     recorded it, rather than a URL-humanized guess)
   *   - collectorCount (distinct userIds)
   *   - photoCount (total displayable photos)
   *   - communityCoverPhotoUrl (best photo, used as the hero fallback)
   *   - type-agnostic metadata carried through when populated
   *     (colorFamily, primaryColor, colors, etc. — only really populated
   *     for types that use them like polish/embellishment/powder).
   */
  polish: Polish;
  /**
   * Grouped photos per contributor. Mirrors PolishContribution[] so the
   * existing PolishPhotoGallery component can render this untouched.
   * Contributors whose profiles are private are excluded.
   */
  contributions: PolishContribution[];
  /**
   * Union of user-added tags across every non-private contributing item.
   * Sorted alphabetically so the chip strip is stable across renders.
   * Rendered on the detail page as a subtle "collector tags" chip row —
   * covers all 8 itemTypes since tags are on user items directly.
   */
  tagsUnion: string[];
}

/**
 * Aggregate community data for a non-polish itemType. Returns null when no
 * matching items exist (item may not be in anyone's collection yet).
 * Capped at 200 contributing items to bound Firestore reads on unusually
 * popular items — mirrors getPolishContributions' bound.
 */
export async function getCommunityItemAggregation(
  itemType: ItemType,
  brandSlug: string,
  nameSlug: string,
  maxItems = 200
): Promise<CommunityItemAggregation | null> {
  let itemsSnap = await getDocs(
    query(
      collectionGroup(db, 'items'),
      where('itemType', '==', itemType),
      where('brandSlug', '==', brandSlug),
      where('nameSlug', '==', nameSlug),
      fbLimit(maxItems)
    )
  );

  // Fallback for legacy items missing brandSlug/nameSlug — the primary
  // query misses them. Retry using brandLower + nameLower (backfilled
  // onto every item by an earlier trigger). Works reliably for brands
  // whose lowercase form equals their slug form (no spaces/punct
  // differences); for names that differ, the `backfillItemSlugs`
  // Cloud Function is still required. This is belt-and-suspenders so
  // simple-brand cases work without an admin action.
  if (itemsSnap.empty) {
    try {
      const fallback = await getDocs(
        query(
          collectionGroup(db, 'items'),
          where('itemType', '==', itemType),
          where('brandLower', '==', brandSlug),
          where('nameLower', '==', nameSlug),
          fbLimit(maxItems)
        )
      );
      if (fallback.empty) return null;
      itemsSnap = fallback;
    } catch {
      return null;
    }
  }

  // Group items by userId (grand-parent of items subcollection is the user
  // doc). Skip brand-catalog items — those live under brands/{id}/items and
  // shouldn't count toward community collectors. Skip soft-deleted items.
  const itemsByUser = new Map<string, any[]>();
  const tagCollector = new Map<string, Set<string>>();
  let firstItemMeta: { name?: string; brand?: string } = {};
  for (const docSnap of itemsSnap.docs) {
    const item = docSnap.data();
    // Skip user-deleted items and ban-hidden items. `hiddenByBan` is set by
    // the onModerationStateChange cascade when the item's owner is banned so
    // their photos + tags stop feeding this aggregation. Also skip items the
    // owner marked private — they don't belong in any cross-user community
    // aggregation, only on the owner's own profile.
    if (item.isDeleted || item.hiddenByBan === true || item.isPrivate === true) continue;
    const grandparent = docSnap.ref.parent.parent;
    if (grandparent?.parent?.id !== 'users') continue;

    // Capture display name/brand from the first surviving item so the
    // page renders with real casing (e.g., "House of Hades" rather than
    // "House Of Hades" derived from a URL slug). Later items would
    // usually match — if they diverge, the first wins here.
    if (!firstItemMeta.name && item.name) firstItemMeta.name = item.name;
    if (!firstItemMeta.brand && item.brand) firstItemMeta.brand = item.brand;

    const userId = grandparent.id;
    const arr = itemsByUser.get(userId) ?? [];
    arr.push(item);
    itemsByUser.set(userId, arr);
    // Collect per-user tags for the union pass below.
    const tags = Array.isArray(item.tags) ? (item.tags as string[]) : [];
    if (tags.length > 0) {
      const set = tagCollector.get(userId) ?? new Set<string>();
      for (const t of tags) {
        if (typeof t === 'string' && t.trim()) set.add(t.trim());
      }
      tagCollector.set(userId, set);
    }
  }

  if (itemsByUser.size === 0) return null;

  // Fetch profile docs to apply per-user privacy. Users with
  // privacy.showCollection === false are excluded from contributions AND
  // from collectorCount — matches how getPolishContributions treats them.
  const userIds = Array.from(itemsByUser.keys());
  const profiles = await Promise.all(
    userIds.map((uid) => getDoc(doc(db, 'users', uid)))
  );

  const contributions: PolishContribution[] = [];
  let totalPhotoCount = 0;
  let communityCoverPhotoUrl: string | undefined;
  const tagsUnionSet = new Set<string>();

  profiles.forEach((snap, i) => {
    if (!snap.exists()) return;
    const profile = snap.data();
    if (profile.privacy?.showCollection === false) return;

    // Union in this user's tags — private users get filtered above so
    // their personal tags don't leak into the public detail-page union.
    const uid = userIds[i];
    const userTags = tagCollector.get(uid);
    if (userTags) for (const t of userTags) tagsUnionSet.add(t);

    const photos: ContributorPhoto[] = [];
    for (const item of itemsByUser.get(uid) ?? []) {
      photos.push(...extractPhotos(item));
    }
    if (photos.length === 0) return;

    totalPhotoCount += photos.length;
    // First photo we see becomes the community cover for the hero fallback.
    if (!communityCoverPhotoUrl) communityCoverPhotoUrl = photos[0].url;

    contributions.push({
      userId: uid,
      username: profile.username,
      displayName: profile.displayName,
      avatarUrl: profile.avatarUrl,
      photos,
    });
  });

  // Stable alphabetical order — chip strip doesn't shuffle across renders.
  const tagsUnion = Array.from(tagsUnionSet).sort((a, b) => a.localeCompare(b));

  // Synthesize a Polish-shaped aggregate. Fields we don't have real values
  // for (verifiedBrandId, officialProductId, primaryColor for a plate,
  // etc.) stay undefined and the shared render handles that.
  const aggregate: Polish = {
    slug: `${brandSlug}__${nameSlug}`,
    brandSlug,
    nameSlug,
    brand: firstItemMeta.brand ?? brandSlug,
    name: firstItemMeta.name ?? nameSlug,
    collectorCount: contributions.length,
    photoCount: totalPhotoCount,
    communityCoverPhotoUrl,
  } as Polish;

  return { polish: aggregate, contributions, tagsUnion };
}

/**
 * A creation that lists a given item as used. Returned by
 * getCreationsFeaturingItem — feeds the "Manis featuring this" section
 * on every item's detail page.
 */
export interface CreationFeaturing {
  id: string;
  userId: string;
  title: string;
  description?: string;
  creationType?: string;
  photoUrl?: string;
  createdAt?: number;
  /** Denormalized creator display info for the section card. */
  username?: string;
  displayName?: string;
  avatarUrl?: string;
}

/**
 * Returns creations that used a given item (any itemType, from any
 * collector). Powered by the denormalized `itemsUsedRefs` array — see
 * utils/creationRefs.ts for format.
 *
 * Uses the `itemsUsedRefs` COLLECTION_GROUP array-contains index.
 * Legacy creations (pre-Phase-8) don't have `itemsUsedRefs` populated
 * and are invisible until the backfill runs. That's expected — the
 * backfill script (functions/src/backfillCreationRefs.ts) fixes them.
 *
 * Excludes deleted creations and creations by users with hidden
 * collections. Capped at `maxCreations` (default 30) to bound reads on
 * unusually popular items.
 */
export async function getCreationsFeaturingItem(
  itemType: ItemType,
  brandSlug: string,
  nameSlug: string,
  maxCreations = 30
): Promise<CreationFeaturing[]> {
  if (!brandSlug || !nameSlug) return [];
  const ref = `${itemType}__${brandSlug}__${nameSlug}`;

  const creationsSnap = await getDocs(
    query(
      collectionGroup(db, 'creations'),
      where('itemsUsedRefs', 'array-contains', ref),
      fbLimit(maxCreations)
    )
  );

  if (creationsSnap.empty) return [];

  // Filter deleted + fetch creator profiles (batched by unique userIds).
  const usable: Array<{ id: string; data: Record<string, any>; userId: string }> = [];
  const userIds = new Set<string>();
  for (const docSnap of creationsSnap.docs) {
    const data = docSnap.data() as Record<string, any>;
    // Skip user-deleted and ban-hidden creations. See getCommunityItemAggregation
    // above for the hiddenByBan cascade rationale.
    if (data.isDeleted || data.hiddenByBan === true) continue;
    const grandparent = docSnap.ref.parent.parent;
    if (grandparent?.parent?.id !== 'users') continue;
    const userId = grandparent.id;
    usable.push({ id: docSnap.id, data, userId });
    userIds.add(userId);
  }

  const profiles = new Map<string, Record<string, any>>();
  await Promise.all(
    Array.from(userIds).map(async (uid) => {
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists()) profiles.set(uid, snap.data());
    })
  );

  const out: CreationFeaturing[] = [];
  for (const c of usable) {
    const profile = profiles.get(c.userId);
    // Use the CREATIONS-specific privacy flag, not the collection one.
    // These are separate settings the user can toggle independently in
    // Settings; the Firestore rule `isCreationsPublic()` mirrors this
    // exact defaulting logic — absent means public. Reusing
    // `showCollection` here (an earlier bug) meant a user who kept their
    // collection stats private but happily shared their creations still
    // got filtered out of Manis-featuring-this.
    const showCreations = profile?.privacy?.showCreations;
    if (showCreations === false) continue;

    const photos: Array<{ url?: string; isDefault?: boolean }> = Array.isArray(c.data.photos)
      ? c.data.photos
      : [];
    const primaryPhoto =
      photos.find((p) => p.isDefault && p.url)?.url ?? photos.find((p) => p.url)?.url;

    out.push({
      id: c.id,
      userId: c.userId,
      title: c.data.title ?? 'Untitled',
      description: c.data.description,
      creationType: c.data.creationType,
      photoUrl: primaryPhoto,
      createdAt:
        typeof c.data.createdAt?.toMillis === 'function'
          ? c.data.createdAt.toMillis()
          : undefined,
      username: profile?.username,
      displayName: profile?.displayName,
      avatarUrl: profile?.avatarUrl,
    });
  }

  return out;
}
